import { useCallback, useRef } from 'react';

import {
  rawTrackEngagementEvent,
  rawTrackViewEvent,
} from '../../../services/tracking/pulse';
import { getPath, pathToUrl, routeNames } from '../../../shared/helpers/routes';
import { pascalCaseToKebabCase } from '../../../utils/string';
import { useVendor } from '../../VendorPovider/VendorPovider';

import type { PulseEvent } from '../../../services/tracking/pulse';
import type { SvpCategoryAdditional } from '@vgtv/api-client/lib/svp_category';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';

export const useSplashTracking = ({
  elementType,
  pageComponentIndex,
}: {
  elementType: string;
  pageComponentIndex?: number;
}) => {
  const { vendor } = useVendor();
  const trackingStateRef = useRef<'idle' | 'viewed' | 'clicked'>('idle');

  const trackSplashClick = useCallback(
    (target: PulseEvent['target']) => {
      if (trackingStateRef.current !== 'viewed') {
        return;
      }

      rawTrackEngagementEvent({
        intent: 'Open',
        object: {
          id: pascalCaseToKebabCase(elementType),
          type: 'UIElement',
          elementType,
          position: String(pageComponentIndex),
        },
        target,
      });

      trackingStateRef.current = 'clicked';
    },
    [elementType, pageComponentIndex]
  );

  const trackAssetSplashClick = useCallback(
    (asset: SvpAssetAdditional) => {
      const url = pathToUrl(
        getPath(routeNames.asset, {
          asset,
          skipDeeplinkCheck: true,
        })
      );

      trackSplashClick({
        id: asset.id,
        name: asset.title,
        type: 'Video',
        category: asset.category.title.toLowerCase(),
        url,
      });
    },
    [trackSplashClick]
  );

  const trackCategorySplashClick = useCallback(
    (category: SvpCategoryAdditional) => {
      const url = pathToUrl(
        getPath(routeNames.category, {
          category,
          vendor,
        })
      );

      trackSplashClick({
        id: category.id,
        name: category.title.toLowerCase(),
        type: 'Category',
        url,
      });
    },
    [trackSplashClick, vendor]
  );

  const trackCustomSplashClick = useCallback(
    (custom: { url: string; title: string }) => {
      trackSplashClick({
        id: 'custom',
        name: custom.title,
        type: 'Custom',
        url: custom.url,
      });
    },
    [trackSplashClick]
  );

  const trackSplashView = useCallback(() => {
    if (trackingStateRef.current !== 'idle') {
      return;
    }

    rawTrackViewEvent({
      object: {
        id: pascalCaseToKebabCase(elementType),
        type: 'UIElement',
        elementType,
        position: String(pageComponentIndex),
      },
    });

    trackingStateRef.current = 'viewed';
  }, [elementType, pageComponentIndex]);

  const trackButtonSplashClick = useCallback(
    (buttonType: string) => {
      rawTrackViewEvent({
        intent: 'Open',
        object: {
          id: pascalCaseToKebabCase(elementType),
          type: 'UIElement',
          elementType: `${elementType}>${buttonType}`,
          position: String(pageComponentIndex),
        },
      });

      trackingStateRef.current = 'clicked';
    },
    [elementType, pageComponentIndex]
  );

  return {
    trackAssetSplashClick,
    trackCategorySplashClick,
    trackCustomSplashClick,
    trackSplashView,
    trackButtonSplashClick,
  };
};
